import React from 'react';
import { Suspense, createContext, useContext, useEffect, useMemo } from 'react';
import { type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

import { useUser } from '@/Utility/hooks';
import useCategorySetter, { type CategoriesType } from '../hooks/dataLoaders/useCategorySetter';
import useStyleLoader from '../hooks/dataLoaders/useStyleLoader';

import {
  AppQuery,
  BackendQuery,
  TopBarNavigationQuery,
  UserQuery,
  type ZnglyContext_App_Query,
  type ZnglyContext_App_Query$data,
  type ZnglyContext_Backend_Query,
  type ZnglyContext_Backend_Query$data,
  type ZnglyContext_TopBarNavigation_Query,
  type ZnglyContext_User_Query,
  appQueryRef,
  topBarNavigationQueryRef,
  userQueryRef,
} from '@/graphql/queries/ZnglyContext.query';

import ZnglySuspense from '@/Components/Suspense';
import { useUserFragment } from '@/graphql/queries/User.query';
import { type AppStore, useAppStore } from '@/inertia/AppStore';
import { ZnglyAppFallback } from '@/inertia/Shared/Page';
import { useAuthStore } from '../auth/Auth';
import useUserLoader, { type UserNode } from '../hooks/dataLoaders/useUserLoader';
import { Chatbot } from '../initApp/dialogflow-cx-messenger/chatbot';

/**
 * App Network Loading Analysis
 *
 * The Important data starts loading immediately
 * - AppQuery
 * - TopBarNavigationQuery
 * - UserQuery
 *
 * They are split into 3 queries to avoid big queries and to better cache the data
 *
 * The entire app is suspended until the User Query is loaded. once loaded then:
 * - The Context Provider will wait for the other two queries to load if they are not already loaded
 * - The Context will set the data the app needs to function
 * - Tanstack Router will also be able to start loading the routes
 *    -> e.g. Home Route: TSR will load its query at this point
 *
 * This flow is optimized for the app to start loading and showing UI as fast as possible
 */

export type ZnglyContextType = {
  // auth dont matter - data anywhere
  appData: {
    queryRef: ZnglyContext_App_Query$data | undefined;
    colorPresets: string[];
    categories: CategoriesType;
  };
  // logged in - data backend only
  backendData: {
    queryRef?: ZnglyContext_Backend_Query$data | null;
    users?: UserNode[];
  };
};

type Props = {
  children: React.ReactNode;
};

// create zngly context
const ZnglyContext = createContext<ZnglyContextType>({
  appData: {
    queryRef: undefined as unknown as ZnglyContext_App_Query$data,
    colorPresets: [],
    categories: { edges: [] },
  },
  backendData: {
    queryRef: null,
    users: [],
  },
});

type BasicContextType = {
  appData: ZnglyContextType['appData'];
  backendQueryRef: PreloadedQuery<ZnglyContext_Backend_Query, Record<string, unknown>>;
} & Props;

const WithBackendDataProvider = ({ children, appData, backendQueryRef }: BasicContextType) => {
  // load data only a logged in user should see in the backend only
  const backendData = usePreloadedQuery<ZnglyContext_Backend_Query>(BackendQuery, backendQueryRef);

  // load users
  const users = useUserLoader(backendData);

  const value: ZnglyContextType = useMemo(
    () => ({
      appData,
      backendData: {
        queryRef: backendData,
        users,
      },
    }),
    [appData, backendData, users]
  );

  return <ZnglyContext.Provider value={value}>{children}</ZnglyContext.Provider>;
};

// pass the values to the context
const ZnglyContextProvider = ({ children }: Props) => {
  // queries that can be loaded
  const [backendQueryRef, loadBackendQuery, disposeBackendQuery] =
    useQueryLoader<ZnglyContext_Backend_Query>(BackendQuery);

  // wait for the app queries to load
  const queryRef = usePreloadedQuery<ZnglyContext_App_Query>(AppQuery, appQueryRef);
  const userQueryData = usePreloadedQuery<ZnglyContext_User_Query>(UserQuery, userQueryRef);
  const topBarQueryData = usePreloadedQuery<ZnglyContext_TopBarNavigation_Query>(
    TopBarNavigationQuery,
    topBarNavigationQueryRef
  );

  // check if we are in the backend
  const pathname = useMemo(() => window.location.pathname, []);

  const isBackend = useMemo(() => {
    const backendUrls = ['/dash'];
    return backendUrls.some((url) => pathname.includes(url));
  }, [pathname]);

  // check if a user is logged in
  const { userExists } = useUser();

  const categories = useCategorySetter(topBarQueryData);
  const userFragment = useUserFragment(userQueryData?.viewer!);
  const { colorPresets } = useStyleLoader(queryRef);

  // load backend query
  useEffect(() => {
    if (!backendQueryRef && userExists === true && isBackend === true)
      loadBackendQuery({}, { fetchPolicy: 'store-or-network' });

    return () => {
      disposeBackendQuery();
    };
  }, [isBackend, userExists]);

  useEffect(() => {
    useAppStore.setState((prev) => ({
      znglyInfo: queryRef?.znglyInfo?.znglyInfo as AppStore['znglyInfo'],
      categories: categories,
      info: {
        ...prev.info,
        canDoAiSearch: !!queryRef?.siteSettings?.canDoAiSearch,
        hasAnalytics: !!queryRef?.znglyInfo?.znglyInfo?.dashboardLink,
      },
      hubs: topBarQueryData?.hubs,
      znglists: topBarQueryData?.znglists,
    }));
  }, [queryRef?.znglyInfo, categories, topBarQueryData?.hubs, topBarQueryData?.znglists]);

  useEffect(() => {
    useAuthStore.getState().setUser(userFragment);
  }, [userFragment]);

  useEffect(() => {
    Chatbot.setChatbotHidden(false);
  }, []);

  const value: ZnglyContextType = useMemo(
    () => ({
      appData: {
        queryRef,
        colorPresets,
        categories,
      },
      backendData: {
        queryRef: null,
        users: [],
      },
    }),
    [queryRef, colorPresets, categories]
  );

  // if a user exists and we are in the backend, then load the backend data
  if (!!backendQueryRef)
    return (
      <ZnglySuspense
        fallback={<ZnglyContext.Provider value={value}>{children}</ZnglyContext.Provider>}
        exists={backendQueryRef}
      >
        {!!backendQueryRef && (
          <WithBackendDataProvider appData={value.appData} backendQueryRef={backendQueryRef}>
            {children}
          </WithBackendDataProvider>
        )}
      </ZnglySuspense>
    );

  return <ZnglyContext.Provider value={value}>{children}</ZnglyContext.Provider>;
};

// export function that returns the zngly context
export const useZnglyContext = () => useContext<ZnglyContextType>(ZnglyContext);

// React Suspense wrapper for the zngly context provider
const ZnglyAppContext = ({ children }: Props) => {
  return (
    <Suspense fallback={<ZnglyAppFallback />}>
      <ZnglyContextProvider>{children}</ZnglyContextProvider>
    </Suspense>
  );
};

const ZnglyAppContextMemo = React.memo(ZnglyAppContext, () => true);

export { ZnglyAppContextMemo as ZnglyAppContext };
